import React, { useMemo, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Badge from 'react-bootstrap/Badge'
import Button from 'react-bootstrap/Button'
import ButtonGroup from 'react-bootstrap/ButtonGroup'
import Table from 'react-bootstrap/Table'
import ApplyPanel from './ApplyPanel'
import Pagination from '../../../component/Pagination'
import useDecisions from './useDecisions'

// Published courses that nobody but their layouts' creators can use: every live layout
// in them is private.
//
// It comes first because every course it unpublishes drops out of all the other
// passes — they only ever see courses somebody other than the creator can see, so
// there is no point naming, trashing or merging one of these first.
//
// There is nothing to judge per row the way there is on the trash tab: the server has
// already established that no public layout is left. "Make all private" is the normal
// way through; a course whose private layouts belong to more than one account is
// highlighted, because that is the one worth a second look.
export default function PrivatePass({ country, data, onDryRun, onApply, applying, dryRunSeen }) {
  const [page, setPage] = useState(0)
  const { decisions, setDecision, decidedCount, clear } = useDecisions('private', country)

  const candidates = useMemo(() => data?.candidates || [], [data])

  if (!data) return null

  const submit = (dryRun) => {
    const payload = Object.entries(decisions)
      .filter(([, action]) => action === 'PRIVATE')
      .map(([courseId]) => {
        const row = candidates.find((c) => String(c.courseId) === String(courseId))
        return { courseId: Number(courseId), oldName: row ? row.name : '' }
      })
    ;(dryRun ? onDryRun : onApply)(payload, dryRun ? undefined : clear)
  }

  const decideAll = () => candidates.forEach((c) => setDecision(c.courseId, 'PRIVATE'))

  return (
    <>
      <Alert variant="light" className="small">
        These courses are published, but every layout in them is private, so nobody except
        the layouts&apos; creators can play them. Making the course private takes it out of
        the catalog and out of every other curation pass. Nothing is deleted, and the
        course&apos;s <em>updated</em> date does not move.
      </Alert>

      {candidates.length > 0 && (
        <div className="mb-2">
          <Button size="sm" variant="outline-primary" onClick={decideAll}>
            Make all {candidates.length} private
          </Button>
        </div>
      )}

      {candidates.length === 0 && <p className="text-muted">Nothing to do in this country.</p>}

      <Pagination items={candidates} page={page} onPage={setPage}>
        {(rows) => (
          <Table size="sm" responsive hover className="curation-table">
            <thead>
              <tr>
                <th>Id</th>
                <th>Name</th>
                <th>Private layouts</th>
                <th>Owners</th>
                <th>Played</th>
                <th>Decision</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const decision = decisions[row.courseId]
                const played = row.layouts.reduce((sum, l) => sum + l.played, 0)
                return (
                  <tr key={row.courseId} className={row.owners > 1 ? 'table-warning' : undefined}>
                    <td>{row.courseId}</td>
                    <td>
                      {row.name}
                      {row.secondaryName && (
                        <span className="text-muted"> · {row.secondaryName}</span>
                      )}
                      {row.authored && (
                        <Badge bg="info" className="ms-2">
                          authored
                        </Badge>
                      )}
                    </td>
                    <td className="small">{row.layouts.map((l) => l.name).join(', ')}</td>
                    <td title={row.layouts.map((l) => `account ${l.accountId}`).join(', ')}>
                      {row.owners}
                    </td>
                    <td>{played}</td>
                    <td>
                      <ButtonGroup size="sm">
                        <Button
                          variant={!decision ? 'secondary' : 'outline-secondary'}
                          onClick={() => setDecision(row.courseId, undefined)}
                        >
                          Leave
                        </Button>
                        <Button
                          variant={decision === 'PRIVATE' ? 'primary' : 'outline-primary'}
                          onClick={() => setDecision(row.courseId, 'PRIVATE')}
                        >
                          Private
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        )}
      </Pagination>

      <ApplyPanel
        decidedCount={decidedCount}
        total={candidates.length}
        dryRunSeen={dryRunSeen}
        applying={applying}
        onDryRun={() => submit(true)}
        onApply={() => submit(false)}
      />
    </>
  )
}
